import { useState, useEffect, useRef } from 'react';
import { projectsAPI, clientsAPI, tasksAPI, photosAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Search, FolderKanban, MoreVertical, Edit, Trash2, Loader2, Calendar, User, ChevronDown, ChevronUp, ListTodo, ImageIcon, Upload, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { format, differenceInDays, parseISO, min as dateMin, max as dateMax, addDays } from 'date-fns';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';

export default function Projects() {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    description: '',
    status: 'active',
    start_date: '',
    end_date: ''
  });
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [projectPhotos, setProjectPhotos] = useState({ before: [], after: [] });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedBeforeIdx, setSelectedBeforeIdx] = useState(0);
  const [selectedAfterIdx, setSelectedAfterIdx] = useState(0);
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        projectsAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      clientsAPI.getAll().then(res => setClients(res.data)).catch(() => {});
    }
  }, [dialogOpen]);

  const fetchProjectPhotos = async (projectId) => {
    try {
      const res = await photosAPI.getByProject(projectId);
      const before = res.data.filter(p => p.photo_type === 'before');
      const after = res.data.filter(p => p.photo_type === 'after');
      setProjectPhotos({ before, after });
      setSelectedBeforeIdx(0);
      setSelectedAfterIdx(0);
    } catch {
      setProjectPhotos({ before: [], after: [] });
    }
  };

  const handlePhotoUpload = async (projectId, photoType, files) => {
    if (!files || files.length === 0) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        await photosAPI.upload(projectId, photoType, file);
      }
      toast.success(`${photoType} photo(s) uploaded`);
      fetchProjectPhotos(projectId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (projectId, photoId) => {
    try {
      await photosAPI.delete(projectId, photoId);
      toast.success('Photo deleted');
      fetchProjectPhotos(projectId);
    } catch {
      toast.error('Failed to delete photo');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      client_id: '',
      description: '',
      status: 'active',
      start_date: '',
      end_date: ''
    });
    setEditingProject(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setFormData({
      name: project.name,
      client_id: project.client_id,
      description: project.description || '',
      status: project.status,
      start_date: project.start_date || '',
      end_date: project.end_date || ''
    });
    setEditingProject(project);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.client_id) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
        toast.success(t('projectUpdated'));
      } else {
        await projectsAPI.create(formData);
        toast.success(t('projectCreated'));
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`${t('delete')} ${project.name}?`)) return;
    try {
      await projectsAPI.delete(project.id);
      toast.success(t('projectDeleted'));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleExpand = async (project) => {
    if (expandedProject === project.id) {
      setExpandedProject(null);
    } else {
      setExpandedProject(project.id);
      try {
        const tasksRes = await tasksAPI.getAll(project.id);
        setProjectTasks(tasksRes.data);
      } catch { setProjectTasks([]); }
      fetchProjectPhotos(project.id);
    }
  };

  const statusLabels = { active: t('active'), in_progress: t('inProgress'), completed: t('completed') };
  
  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-[#78866B]/10 text-[#78866B]',
      in_progress: 'bg-[#C98E75]/10 text-[#C98E75]',
      completed: 'bg-[#57534E]/10 text-[#57534E]'
    };
    return <Badge className={`${styles[status] || styles.active} border-0`}>{statusLabels[status] || status}</Badge>;
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.client_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'active') return matchesSearch && (p.status === 'active' || p.status === 'in_progress');
    return matchesSearch && p.status === activeTab;
  });

  // Timeline helpers
  const projectsWithDates = projects.filter(p => p.start_date);
  const getTimelineRange = () => {
    if (projectsWithDates.length === 0) return { start: new Date(), end: addDays(new Date(), 90) };
    const starts = projectsWithDates.map(p => parseISO(p.start_date));
    const ends = projectsWithDates.map(p => p.end_date ? parseISO(p.end_date) : addDays(parseISO(p.start_date), 30));
    const earliest = dateMin(starts);
    const latest = dateMax(ends);
    return { start: addDays(earliest, -7), end: addDays(latest, 14) };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const timelineRange = getTimelineRange();
  const totalDays = differenceInDays(timelineRange.end, timelineRange.start) || 1;

  const statusColors = {
    active: '#78866B',
    in_progress: '#C98E75',
    completed: '#57534E'
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('projects')}</h1>
          <p className="text-[#78716C] mt-1">{projects.length} {t('totalProjectsCount')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
              onClick={openAddDialog}
              data-testid="add-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('newProject')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                {editingProject ? t('editProject') : t('createNewProject')}
              </DialogTitle>
              <DialogDescription className="sr-only">Project form</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('projectName')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('projectName')}
                  className="h-11"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client">{t('client')} *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger className="h-11" data-testid="project-client-select">
                    <SelectValue placeholder={t('selectClient')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('description')}
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">{t('startDate')}</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="h-11"
                    data-testid="project-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">{t('endDate')}</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="h-11"
                    data-testid="project-end-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">{t('status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="h-11" data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('active')}</SelectItem>
                    <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
                    <SelectItem value="completed">{t('completed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                  disabled={saving}
                  data-testid="save-project-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingProject ? t('update') : t('create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
        <Input
          placeholder={t('searchProjects')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl"
          data-testid="search-projects-input"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F5F5F4]">
          <TabsTrigger value="active" data-testid="active-projects-tab">{t('active')}</TabsTrigger>
          <TabsTrigger value="completed" data-testid="completed-projects-tab">{t('completed')}</TabsTrigger>
          <TabsTrigger value="all" data-testid="all-projects-tab">{t('all')}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="timeline-tab">{t('timeline')}</TabsTrigger>
        </TabsList>

        {/* Timeline View */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="border-0 shadow-sm" data-testid="project-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium">{t('projectTimeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              {projectsWithDates.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                  <p className="text-[#78716C]">{t('noProjectDates')}</p>
                  <p className="text-sm text-[#A8A29E] mt-1">{t('addDatesToProjects')}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Month headers */}
                  <div className="flex border-b border-[#E7E5E4] pb-2 mb-4 text-xs text-[#78716C]">
                    <div className="w-44 shrink-0" />
                    <div className="flex-1 relative h-5 overflow-hidden">
                      {Array.from({ length: Math.ceil(totalDays / 30) + 1 }, (_, i) => {
                        const d = addDays(timelineRange.start, i * 30);
                        const leftPct = (differenceInDays(d, timelineRange.start) / totalDays) * 100;
                        return (
                          <span key={i} className="absolute text-xs font-medium" style={{ left: `${leftPct}%` }}>
                            {format(d, 'MMM yyyy')}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {/* Project bars */}
                  {projectsWithDates.map((project) => {
                    const start = parseISO(project.start_date);
                    const end = project.end_date ? parseISO(project.end_date) : addDays(start, 30);
                    const leftPct = Math.max(0, (differenceInDays(start, timelineRange.start) / totalDays) * 100);
                    const widthPct = Math.max(2, (differenceInDays(end, start) / totalDays) * 100);
                    const color = statusColors[project.status] || statusColors.active;
                    const isToday = differenceInDays(new Date(), timelineRange.start);
                    
                    return (
                      <div key={project.id} className="flex items-center py-2 group hover:bg-[#F5F5F4]/50 rounded-lg px-1" data-testid={`timeline-bar-${project.id}`}>
                        <div className="w-44 shrink-0 pr-3">
                          <p className="text-sm font-medium text-[#292524] truncate">{project.name}</p>
                          <p className="text-xs text-[#78716C] truncate">{project.client_name}</p>
                        </div>
                        <div className="flex-1 relative h-8">
                          {/* Today marker */}
                          <div
                            className="absolute top-0 bottom-0 w-px bg-red-300 z-10"
                            style={{ left: `${(isToday / totalDays) * 100}%` }}
                          />
                          <div
                            className="absolute top-1 h-6 rounded-full transition-all duration-300 flex items-center px-2"
                            style={{
                              left: `${leftPct}%`,
                              width: `${widthPct}%`,
                              backgroundColor: `${color}20`,
                              border: `1.5px solid ${color}`,
                              minWidth: '40px'
                            }}
                          >
                            <span className="text-xs font-medium truncate" style={{ color }}>
                              {format(start, 'MMM d')} - {format(end, 'MMM d')}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Today legend */}
                  <div className="flex items-center gap-2 pt-3 mt-2 border-t border-[#E7E5E4] text-xs text-[#78716C]">
                    <div className="w-3 h-px bg-red-300" /> {t('today')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value={activeTab === 'timeline' ? '__hidden__' : activeTab} className={activeTab === 'timeline' ? 'hidden' : 'mt-6'}>
          {filteredProjects.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <FolderKanban className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#78716C]">
                  {searchQuery ? t('noProjectsFound') : t('noProjectsYet')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-10">
              {filteredProjects.map((project, index) => (
                <Card 
                  key={project.id}
                  className={`border-0 shadow-sm hover:shadow-md transition-all duration-200 animate-slide-in stagger-${Math.min(index + 1, 5)} cursor-pointer mb-5`}
                  data-testid={`project-card-${project.id}`}
                  onClick={() => toggleExpand(project)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-[#292524] text-lg">{project.name}</h3>
                          {getStatusBadge(project.status)}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#78716C]">
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4 shrink-0" />
                            <span>{project.client_name || t('unknownClient')}</span>
                          </div>
                          {project.start_date && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 shrink-0" />
                              <span className="whitespace-nowrap">
                                {format(new Date(project.start_date), 'MMM d, yyyy')}
                                {project.end_date && ` - ${format(new Date(project.end_date), 'MMM d, yyyy')}`}
                              </span>
                            </div>
                          )}
                        </div>
                        {project.description && expandedProject !== project.id && (
                          <p className="text-sm text-[#78716C] mt-3 line-clamp-2">{project.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`project-menu-${project.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(project); }} data-testid={`edit-project-${project.id}`}>
                              <Edit className="w-4 h-4 mr-2" />
                              {t('edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDelete(project); }}
                              className="text-red-600"
                              data-testid={`delete-project-${project.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {expandedProject === project.id ? (
                          <ChevronUp className="w-4 h-4 text-[#78716C]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#78716C]" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedProject === project.id && (
                      <div className="mt-4 pt-4 border-t border-[#E7E5E4] space-y-4" onClick={(e) => e.stopPropagation()} data-testid={`project-expanded-${project.id}`}>
                        {project.description && (
                          <div>
                            <h4 className="text-xs font-medium text-[#78716C] uppercase tracking-wide mb-1">{t('description')}</h4>
                            <p className="text-sm text-[#292524]">{project.description}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#78716C]">{t('client')}:</span>
                            <span className="ml-2 text-[#292524] font-medium">{project.client_name || '-'}</span>
                          </div>
                          <div>
                            <span className="text-[#78716C]">{t('status')}:</span>
                            <span className="ml-2">{getStatusBadge(project.status)}</span>
                          </div>
                        </div>

                        {/* Tasks */}
                        <div>
                          <h4 className="text-xs font-medium text-[#78716C] uppercase tracking-wide mb-2 flex items-center gap-1">
                            <ListTodo className="w-3.5 h-3.5" /> {t('tasks')} ({projectTasks.length})
                          </h4>
                          {projectTasks.length === 0 ? (
                            <p className="text-sm text-[#A8A29E]">{t('noTasks')}</p>
                          ) : (
                            <div className="space-y-1.5">
                              {projectTasks.map(task => (
                                <div key={task.id} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-[#F5F5F4]/50">
                                  <span className={`w-2 h-2 rounded-full ${task.status === 'done' ? 'bg-[#57534E]' : task.status === 'in_progress' ? 'bg-[#C98E75]' : 'bg-[#78866B]'}`} />
                                  <span className="text-[#292524] flex-1 truncate">{task.title}</span>
                                  <span className="text-xs text-[#A8A29E] capitalize">{task.status.replace('_', ' ')}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Before & After Photos */}
                        <div>
                          <h4 className="text-xs font-medium text-[#78716C] uppercase tracking-wide mb-3 flex items-center gap-1">
                            <ImageIcon className="w-3.5 h-3.5" /> {t('beforeAfter')}
                          </h4>
                          
                          {/* Before/After Slider */}
                          {projectPhotos.before.length > 0 && projectPhotos.after.length > 0 && (
                            <div className="mb-4">
                              <BeforeAfterSlider
                                beforeUrl={photosAPI.getFileUrl(projectPhotos.before[selectedBeforeIdx]?.storage_path)}
                                afterUrl={photosAPI.getFileUrl(projectPhotos.after[selectedAfterIdx]?.storage_path)}
                                height={300}
                              />
                              {/* Thumbnail selectors */}
                              {(projectPhotos.before.length > 1 || projectPhotos.after.length > 1) && (
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                  <div>
                                    <p className="text-xs text-[#78716C] mb-1">Before</p>
                                    <div className="flex gap-1 overflow-x-auto">
                                      {projectPhotos.before.map((p, i) => (
                                        <button
                                          key={p.id}
                                          onClick={() => setSelectedBeforeIdx(i)}
                                          className={`w-10 h-10 rounded-lg overflow-hidden border-2 shrink-0 ${i === selectedBeforeIdx ? 'border-[#78866B]' : 'border-transparent'}`}
                                        >
                                          <img src={photosAPI.getFileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-xs text-[#78716C] mb-1">After</p>
                                    <div className="flex gap-1 overflow-x-auto">
                                      {projectPhotos.after.map((p, i) => (
                                        <button
                                          key={p.id}
                                          onClick={() => setSelectedAfterIdx(i)}
                                          className={`w-10 h-10 rounded-lg overflow-hidden border-2 shrink-0 ${i === selectedAfterIdx ? 'border-[#78866B]' : 'border-transparent'}`}
                                        >
                                          <img src={photosAPI.getFileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Photo galleries & upload buttons */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Before */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-[#292524]">Before ({projectPhotos.before.length}/10)</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => beforeInputRef.current?.click()}
                                  disabled={uploadingPhoto || projectPhotos.before.length >= 10}
                                  data-testid="upload-before-btn"
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Upload
                                </Button>
                                <input
                                  ref={beforeInputRef}
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handlePhotoUpload(project.id, 'before', e.target.files)}
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-1.5">
                                {projectPhotos.before.map((photo) => (
                                  <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5F5F4]">
                                    <img src={photosAPI.getFileUrl(photo.storage_path)} alt="" className="w-full h-full object-cover" />
                                    <button
                                      onClick={() => handlePhotoDelete(project.id, photo.id)}
                                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                      data-testid={`delete-photo-${photo.id}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* After */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-[#292524]">After ({projectPhotos.after.length}/10)</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => afterInputRef.current?.click()}
                                  disabled={uploadingPhoto || projectPhotos.after.length >= 10}
                                  data-testid="upload-after-btn"
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Upload
                                </Button>
                                <input
                                  ref={afterInputRef}
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handlePhotoUpload(project.id, 'after', e.target.files)}
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-1.5">
                                {projectPhotos.after.map((photo) => (
                                  <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5F5F4]">
                                    <img src={photosAPI.getFileUrl(photo.storage_path)} alt="" className="w-full h-full object-cover" />
                                    <button
                                      onClick={() => handlePhotoDelete(project.id, photo.id)}
                                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                      data-testid={`delete-photo-${photo.id}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          {uploadingPhoto && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-[#78716C]">
                              <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
